import { useCallback, useEffect, useState } from 'react';
import Button from 'devextreme-react/button';
import { MyStorageFolderPickerPopup } from '../fileBrowser/MyStorageFolderPickerPopup';
import {
  DEFAULT_CONTENT_FOLDER_KEY,
  DEFAULT_CONTENT_FOLDER_PATH,
  DEFAULT_CONTENT_FOLDER_PREFS_CHANGED,
  formatDefaultContentFolderDisplay,
  readDefaultContentFolderPath,
  resetDefaultContentFolderPath,
  writeDefaultContentFolderPath
} from '../fileBrowser/defaultContentFolderPrefs';
import '../theme/themePreferences.css';

export function DefaultContentFolderPreferencesPanel() {
  const [folderPath, setFolderPath] = useState(() => readDefaultContentFolderPath());
  const [pickerOpen, setPickerOpen] = useState(false);

  const syncFromStorage = useCallback(() => {
    setFolderPath(readDefaultContentFolderPath());
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === DEFAULT_CONTENT_FOLDER_KEY) syncFromStorage();
    };
    const onCustom = () => syncFromStorage();
    window.addEventListener('storage', onStorage);
    window.addEventListener(DEFAULT_CONTENT_FOLDER_PREFS_CHANGED, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(DEFAULT_CONTENT_FOLDER_PREFS_CHANGED, onCustom);
    };
  }, [syncFromStorage]);

  const onSelectFolder = useCallback((nextPath: string) => {
    writeDefaultContentFolderPath(nextPath);
    setFolderPath(readDefaultContentFolderPath());
    setPickerOpen(false);
  }, []);

  const onReset = useCallback(() => {
    resetDefaultContentFolderPath();
    setFolderPath(readDefaultContentFolderPath());
  }, []);

  const isDefault = folderPath === DEFAULT_CONTENT_FOLDER_PATH;

  return (
    <section
      className="theme-preferences theme-preferences--first"
      aria-labelledby="default-content-folder-preferences-heading"
    >
      <h3 id="default-content-folder-preferences-heading" className="theme-preferences__title">
        Default save folder
      </h3>
      <p className="theme-preferences__hint">
        New Document, Spreadsheet, Text, Markdown, Draw.io, and Code files are created in this
        My Storage folder. The factory default is <strong>Workspace</strong> (created automatically
        if it does not exist yet).
      </p>

      <div className="theme-preferences__section">
        <span className="theme-preferences__label">Current folder</span>
        <p className="theme-preferences__hint theme-preferences__hint--tight" aria-live="polite">
          {formatDefaultContentFolderDisplay(folderPath)}
        </p>
      </div>

      <div className="theme-preferences__actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Button text="Choose folder…" type="default" onClick={() => setPickerOpen(true)} />
        <Button
          text="Reset to Workspace"
          stylingMode="outlined"
          disabled={isDefault}
          onClick={onReset}
        />
      </div>

      <MyStorageFolderPickerPopup
        visible={pickerOpen}
        initialPath={folderPath}
        onClose={() => setPickerOpen(false)}
        onSelect={onSelectFolder}
      />
    </section>
  );
}
